import { supabase } from '@/integrations/supabase/client';
import { scheduleAppointmentReminders } from './appointmentReminders';
import { 
  formatAppointmentDate, 
  formatAppointmentTime 
} from '@/lib/mailchimp-calendar';

export interface FormSubmissionData {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  message?: string;
  source: 'contact_form' | 'demo_request' | 'quote_request' | 'home_internet';
  servicesInterested?: string[];
  isDemoRequest?: boolean;
  demoDate?: string;
  demoTime?: string;
}

export interface FormSubmissionResult {
  success: boolean;
  message: string;
  contactId?: string;
  error?: any;
}

export const submitForm = async (data: FormSubmissionData): Promise<FormSubmissionResult> => {
  console.log('Form submission started');
  
  try {
    // Use the secure edge function for form submission with proper validation
    const { data: result, error: submitError } = await supabase.functions.invoke('submit-form', {
      body: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || null,
        company: data.company || null,
        message: data.message || null,
        source: data.source,
        servicesInterested: data.servicesInterested || [],
        demoDate: data.demoDate || null,
        demoTime: data.demoTime || null,
        isDemoRequest: data.isDemoRequest || false
      }
    });

    if (submitError) {
      console.error('Form submission failed');
      return {
        success: false,
        message: 'Failed to submit form. Please try again or call us at (833) 3-SIMNET.'
      };
    }
    
    const contact = { id: result.contactId };

    // If this is a demo request with date/time, send confirmation and schedule reminders
    if ((data.source === 'demo_request' || data.isDemoRequest) && data.demoDate && data.demoTime && result.appointmentId) {
      // Send immediate confirmation email via Mailchimp edge function (server-side)
      try {
        await supabase.functions.invoke('send-confirmation-email', {
          body: {
            customerEmail: data.email,
            customerName: `${data.firstName} ${data.lastName}`,
            appointmentDate: formatAppointmentDate(data.demoDate),
            appointmentTime: formatAppointmentTime(data.demoTime, data.demoDate),
            appointmentId: result.appointmentId
          }
        });
        console.log('✅ Confirmation email sent via Mailchimp (server-side)');
      } catch (emailError) {
        console.error('❌ Failed to send confirmation email:', emailError);
        // Don't fail the booking if email fails
      }

      // Send internal team notification (non-blocking)
      try {
        await supabase.functions.invoke('send-internal-booking-notification', {
          body: {
            customerName: `${data.firstName} ${data.lastName}`,
            customerEmail: data.email,
            customerPhone: data.phone || '',
            customerCompany: data.company || '',
            customerMessage: data.message || '',
            appointmentDate: formatAppointmentDate(data.demoDate),
            appointmentTime: formatAppointmentTime(data.demoTime, data.demoDate),
            appointmentId: result.appointmentId
          }
        });
        console.log('✅ Internal team notification sent');
      } catch (emailError) {
        console.error('❌ Failed to send internal notification:', emailError);
        // Don't fail the booking if internal email fails
      }

      // Schedule reminder emails (both customer and internal)
      await scheduleAppointmentReminders({
        appointmentId: result.appointmentId,
        contactId: contact.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        phone: data.phone || '',
        scheduledDate: data.demoDate,
        scheduledTime: data.demoTime,
      });
    }

    // Trigger instant email processing
    try {
      await supabase.functions.invoke('process-email-notifications', {
        body: {}
      });
    } catch (emailTriggerError) {
      console.warn('Could not trigger email processing');
    }

    // Trigger instant Slack notification processing
    try {
      await supabase.functions.invoke('send-slack-notifications', {
        body: {}
      });
    } catch (slackTriggerError) {
      console.warn('Could not trigger Slack notification');
    }

    return {
      success: true,
      message: 'Thank you! We\'ll be in touch shortly.',
      contactId: contact.id
    };

  } catch (error) {
    console.error('Form submission error');
    
    return {
      success: false,
      message: 'There was an error submitting your form. Please try again or call us at (833) 3-SIMNET.'
    };
  }
};
